import { isDemo } from "../App";
import { axiosRequest } from "../axiosConfig";
// Import pre-cached PDF data from data.json
import preCachedData from "./data.json";

// PDF Cache Manager with fallback mechanisms
class PDFCacheManager {
  constructor() {
    this.cache = new Map();
    this.maxCacheSize = 50; // Maximum number of PDFs to cache
  }

  async readFromCache(cacheKey) {
    try {
      // 1. Check the pre-cached JSON file first - use the cacheKey directly
      // This works for both parent faxes and child faxes with the format faxId_childId
      if (preCachedData && preCachedData[cacheKey]) {
        console.log(`Found PDF in pre-cached JSON data: ${cacheKey}`);
        const cacheEntry = preCachedData[cacheKey];
        // Validate that we have content to work with
        if (cacheEntry.content && Array.isArray(cacheEntry.content)) {
          // Store it in our in-memory cache for faster access later
          this.cache.set(cacheKey, cacheEntry);
          return new Blob([new Uint8Array(cacheEntry.content)], {
            type: "application/pdf",
          });
        } else {
          console.warn(`Found entry in pre-cached data but no valid content: ${cacheKey}`);
        }
      }

      // 2. Check in-memory cache
      if (this.cache.has(cacheKey)) {
        console.log(`Found PDF in memory cache: ${cacheKey}`);
        const cacheEntry = this.cache.get(cacheKey);
        return new Blob([new Uint8Array(cacheEntry.content)], {
          type: "application/pdf",
        });
      }

      // 3. Check session storage as final fallback
      if (typeof sessionStorage !== "undefined") {
        const storedCache = sessionStorage.getItem("pdfData");
        if (storedCache) {
          const parsedCache = JSON.parse(storedCache);
          if (parsedCache[cacheKey] && parsedCache[cacheKey].content) {
            console.log(`Found PDF in session storage: ${cacheKey}`);
            this.cache.set(cacheKey, parsedCache[cacheKey]);
            return new Blob([new Uint8Array(parsedCache[cacheKey].content)], {
              type: "application/pdf",
            });
          }
        }
      }

      console.log(`PDF not found in any cache: ${cacheKey}`);
      return null;
    } catch (error) {
      console.error(`Error reading from cache for ${cacheKey}:`, error);
      return null;
    }
  }

  async writeToCache(cacheKey, data) {
    try {
      // Convert blob to array buffer
      const arrayBuffer = await data.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const cacheEntry = {
        content: Array.from(uint8Array),
        timestamp: new Date().toISOString(),
      };

      // Update in-memory cache
      this.cache.set(cacheKey, cacheEntry);

      // Maintain cache size limit
      if (this.cache.size > this.maxCacheSize) {
        // Find and remove oldest entry
        let oldestKey = null;
        let oldestTime = new Date();

        this.cache.forEach((entry, key) => {
          const entryTime = new Date(entry.timestamp);
          if (entryTime < oldestTime) {
            oldestTime = entryTime;
            oldestKey = key;
          }
        });

        if (oldestKey) {
          this.cache.delete(oldestKey);
          console.log(`Removed oldest cache entry: ${oldestKey}`);
        }
      }

      // Update persistent storage using sessionStorage
      if (typeof sessionStorage !== "undefined") {
        try {
          const persistentCache = {};
          this.cache.forEach((value, key) => {
            persistentCache[key] = value;
          });
          sessionStorage.setItem(
            "pdfData",
            JSON.stringify(persistentCache),
          );
          console.log(`Cache updated successfully in sessionStorage: ${cacheKey}`);
        } catch (err) {
          console.log("Error writing to persistent cache:", err);
        }
      }
    } catch (error) {
      console.error(`Error writing to cache for ${cacheKey}:`, error);
    }
  }

  async fetchPdfWithCache(url, faxId, isChild = false, childId = null) {
    try {
      // Create the appropriate cache key (matches the structure in data.json)
      const cacheKey = isChild ? `${faxId}_${childId}` : faxId;

      // Try to get from cache first
      const cachedPdf = await this.readFromCache(cacheKey);
      if (isDemo && cachedPdf) {
        console.log(`Using cached PDF for demo mode: ${cacheKey}`);
        return cachedPdf;
      }

      // If not in cache or not in demo mode, fetch from API
      console.log(`Fetching PDF from API: ${url}`);
      const response = await axiosRequest.get(url, {
        responseType: "blob",
      });

      // Validate the response
      if (response.data && response.data.size > 0) {
        // Cache the response
        await this.writeToCache(cacheKey, response.data);
        return response.data;
      } else {
        throw new Error("Received empty or invalid PDF from API");
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const pdfCacheManager = new PDFCacheManager();